import { ENDPOINT_NAMES } from '../../../constants';
import type { FieldingGroupRead, StepBase } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PatchStepOrdinalsArgs {
  fieldingGroupId: string;
  body: StepBase[];
}

interface PatchStepOrdinalsContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: StepBase[] }>;
  getState: () => RootState;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type FieldingGroupQueryArgs = {
  projectId: number;
  highlighterId?: string;
  expand?: boolean;
};

/**
 * ### findProjectIdFromCachedQueries
 *
 * @description
 * Searches through cached fielding group queries to find the projectId
 * for a given fieldingGroupId.
 *
 * @param queries - The queries object from RTK Query state
 * @param fieldingGroupId - The fielding group ID to search for
 * @returns The projectId if found, undefined otherwise
 */
const findProjectIdFromCachedQueries = (
  queries: Record<string, unknown> | undefined,
  fieldingGroupId: string,
): number | undefined => {
  if (!queries) return undefined;

  // Search through all queries to find fielding group queries
  for (const [, query] of Object.entries(queries)) {
    const cachedQuery = query as {
      endpointName?: string;
      originalArgs?: FieldingGroupQueryArgs;
      data?: FieldingGroupRead[];
    };

    if (
      cachedQuery?.endpointName === ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP &&
      cachedQuery?.data &&
      Array.isArray(cachedQuery.data)
    ) {
      // Check if this query contains the fielding group we're looking for
      const fieldingGroup = cachedQuery.data.find((fg) => fg.id === fieldingGroupId);
      if (fieldingGroup) {
        // Return projectId from query args or from the fielding group itself
        return cachedQuery.originalArgs?.projectId ?? fieldingGroup.projectId;
      }
    }
  }

  return undefined;
};

/**
 * ### updateStepsInFieldingGroupCache
 *
 * @description
 * Updates the steps array for a specific fielding group in a draft array.
 *
 * @param draft - The draft array containing fielding groups
 * @param fieldingGroupId - The ID of the fielding group to update
 * @param steps - The new steps array
 */
const updateStepsInFieldingGroupCache = (
  draft: FieldingGroupRead[] | undefined,
  fieldingGroupId: string,
  steps: StepBase[],
): void => {
  if (!draft) return;

  const fieldingGroup = draft.find((fg) => fg.id === fieldingGroupId);
  if (fieldingGroup) {
    fieldingGroup.steps = steps;
  }
};

/**
 * ### Patch Step Ordinals Enhancer
 *
 * @description
 * Enhancer for patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals endpoint.
 * Optimistically updates the steps in the cache before the server responds.
 * Updates both the direct steps query and the steps array within fielding groups.
 * If the request fails, the update is rolled back.
 *
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals = (api: ProjectApi) => ({
  // Disable tag invalidation since we're using optimistic updates
  // The generated RTK code has invalidatesTags: ['Steps'] which would
  // cause the GET query to refetch and override our optimistic update
  invalidatesTags: () => [],
  async onQueryStarted(args: PatchStepOrdinalsArgs, context: PatchStepOrdinalsContext) {
    const { fieldingGroupId, body: steps } = args;
    const { dispatch, queryFulfilled, getState } = context;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Update the direct steps query cache
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_FIELDING_GROUPS_BY_FIELDING_GROUP_ID_STEPS,
      args: { fieldingGroupId },
      updateFn: (draft: StepBase[]) => {
        // Replace the entire steps array with the new ordered steps
        draft.splice(0, draft.length, ...steps);
      },
    });

    // Find projectId by searching cached fielding group queries
    const state = getState();
    const queries = state.projectApi?.queries;
    const projectId = findProjectIdFromCachedQueries(queries, fieldingGroupId);

    if (projectId) {
      // Find all matching cached fielding group queries for this project
      // This handles queries with different highlighterId/expand parameters
      const matchingQueries = findMatchingCachedQueries<FieldingGroupQueryArgs>({
        queries,
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
        matcher: (queryArgs) => queryArgs.projectId === projectId,
      });

      // Update all matching cached queries (handles variations like different expand/highlighterId values)
      matchingQueries.forEach((queryArgs) => {
        updater.update({
          endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
          args: queryArgs,
          updateFn: (draft: FieldingGroupRead[]) =>
            updateStepsInFieldingGroupCache(draft, fieldingGroupId, steps),
        });
      });

      // Also update the most common query pattern directly
      updater.update({
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
        args: { projectId, expand: true },
        updateFn: (draft: FieldingGroupRead[]) =>
          updateStepsInFieldingGroupCache(draft, fieldingGroupId, steps),
      });
    }

    try {
      await queryFulfilled;
    } catch {
      // Rollback all optimistic updates on failure
      updater.rollback();
    }
  },
});

export default patchFieldingGroupsByFieldingGroupIdStepsUpdateOrdinals;
